<template>
  <div class="view" v-if="canEdit || canDelete || canView" v-cloak>
    <GridPanelHeader
      :title="$t('common.transactions')"
      :grid-ref="$refs[gridRefKey]"
      :show-add="false"
      :show-filter="true"
      :show-export="true"
      :show-columns="true"
      :show-refresh="true"
      @filter="openFilterPanel"
    >
      <template #actions>
        <stock-actions></stock-actions>
      </template>
    </GridPanelHeader>

    <GridFilter
      :visible="filterPanelVisible"
      :title="$t('common.filter')"
      @apply="applyFilters"
      @reset="resetFilters"
      @close="closeFilterPanel"
    >
      <div class="filter-field no-border">
        <label>{{ $t("stock_management.transactions.transaction_type") }}</label>
        <DxTagBox
          :data-source="filteredSmTransactionTypes"
          value-expr="uuid"
          display-expr="name"
          v-model="filters.transaction_types"
          :showClearButton="true"
          :searchEnabled="true"
          :applyValueMode="'instantly'"
          :showSelectionControls="true"
          :selectAllMode="'allPages'"
          :selectAllText="$t('select_all')"
          :noDataText="$t('common.no_data_found')"
          :placeholder="$t('stock_management.transactions.transaction_type')"
        />
      </div>
      <DateRangeFilter :dateFrom.sync="filters.date_from" :dateTo.sync="filters.date_to" />
    </GridFilter>

    <DxDataGrid
      ref="smTransactionsGrid"
      :data-source="transactionsDataSource"
      :column-min-width="50"
      :show-column-lines="true"
      :show-row-lines="false"
      :show-borders="true"
      :column-auto-width="true"
      :allow-column-reordering="true"
      :allow-column-resizing="true"
      :row-alternation-enabled="true"
      :remote-operations="gridRemoteOperations"
      :element-attr="gridAttributes"
      :load-panel="{ enabled: true }"
      :no-data-text="$t('common.no_data_text')"
      @option-changed="onOptionChanged"
      @row-prepared="onRowPrepared"
      @exporting="onExporting"
    >
      <DxColumn
        :width="80"
        alignment="center"
        :fixed="true"
        fixed-position="left"
        :allow-sorting="false"
        :allow-filtering="false"
        :show-in-column-chooser="false"
        :css-class="'sm-transactions-pdf-col'"
        cell-template="pdfCell"
      />
      <template #pdfCell="{ data }">
        <div>
          <div
            v-if="data.data.document"
            class="transaction-pdf-link"
            :class="{ 'has-erp-status': shouldShowErpStatus(data.data) }"
            :title="getPdfLinkTitle(data.data, false)"
            @click.prevent="handleDownloadPdf(data.data, false)"
          >
            <img
              src="~@/assets/images/desktop/icons/controls/pdf.svg"
              class="icon-svg danger-filter"
            />
            <span
              v-if="shouldShowErpStatus(data.data)"
              class="erp-status-badge"
              :class="getErpStatusBadgeClass(data.data)"
              :title="getErpStatusTitle(data.data)"
            >
              <svg v-if="isErpSuccess(data.data)" viewBox="0 0 24 24" width="9" height="9" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
              <svg v-else-if="isErpError(data.data)" viewBox="0 0 24 24" width="9" height="9" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
              <svg v-else viewBox="0 0 24 24" width="9" height="9" fill="currentColor">
                <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
            </span>
          </div>
          <div
            v-if="data.data.cancel_document"
            class="transaction-pdf-link"
            :title="getPdfLinkTitle(data.data, true)"
            @click.prevent="handleDownloadPdf(data.data, true)"
          >
            <img
              src="~@/assets/images/desktop/icons/controls/pdf.svg"
              class="icon-svg danger-filter"
            />
          </div>
          <div
            v-if="data.data.delivery_note_download_url"
            class="transaction-pdf-link"
            :title="
              $t('stock_management.transactions.delivery_note') +
              '\n' +
              $t('common.download_document_pdf', {
                document: data.data.delivery_note_download_filename,
              })
            "
            @click.prevent="handleDownloadDeliveryNote(data.data)"
          >
            <img
              src="~@/assets/images/desktop/icons/controls/pdf.svg"
              class="icon-svg danger-filter"
            />
          </div>
          <div v-else-if="!data.data.document && !data.data.cancel_document" style="width: 25px"></div>
        </div>
      </template>

      <DxColumn
        data-field="product.name"
        :caption="$t('common.product')"
        data-type="string"
        :allow-sorting="true"
        :filter-operations="['contains']"
        :min-width="180"
      />

      <DxColumn
        data-field="product.barcode"
        :caption="$t('stock_management.products.barcode')"
        data-type="string"
        :allow-sorting="true"
        :filter-operations="['contains']"
        :min-width="110"
        :visible="false"
      />

      <DxColumn
        data-field="product.code"
        :caption="$t('stock_management.products.code')"
        data-type="string"
        :allow-sorting="true"
        :filter-operations="['contains']"
        :min-width="110"
      />

      <DxColumn
        data-field="transaction_type"
        :caption="$t('stock_management.transactions.transaction_type')"
        data-type="string"
        :allow-sorting="false"
        :allow-filtering="true"
        :filter-operations="['=']"
        :min-width="130"
        cell-template="transactionTypeViewTemplate"
        :calculateCellValue="getTransactionTypeName"
      >
        <DxLookup :data-source="filteredSmTransactionTypes" value-expr="uuid" display-expr="name" />
      </DxColumn>
      <template #transactionTypeViewTemplate="{ data }">
        <span>
          {{ getTransactionTypeName(data.data) }}
        </span>
      </template>

      <DxColumn
        data-field="is_canceled"
        :caption="$t('deleted')"
        :allow-sorting="false"
        :allow-filtering="true"
        :filter-operations="['=']"
        :min-width="100"
        :calculate-cell-value="getCanceledYesNo"
        cell-template="canceledAtCell"
      >
        <DxLookup :data-source="yes_no_list" value-expr="id" display-expr="label" />
      </DxColumn>
      <template #canceledAtCell="{ data }">
        <span>
          {{ getYesNoColumnText(getCanceledYesNo(data.data)) }}
        </span>
      </template>

      <DxColumn
        data-field="has_document"
        :caption="$t('stock_management.transactions.has_document')"
        :allow-sorting="false"
        :allow-filtering="true"
        :filter-operations="['=']"
        :min-width="120"
        :calculate-cell-value="getHasDocumentYesNo"
        cell-template="hasDocumentCell"
      >
        <DxLookup :data-source="yes_no_list" value-expr="id" display-expr="label" />
      </DxColumn>
      <template #hasDocumentCell="{ data }">
        <span>
          {{ getYesNoColumnText(getHasDocumentYesNo(data.data)) }}
        </span>
      </template>

      <DxColumn
        data-field="transaction_date"
        :caption="$t('stock_management.transactions.date')"
        data-type="date"
        :allow-sorting="true"
        :filter-operations="['=']"
        :min-width="120"
        :format="getDxGridDefaultDateFormat"
        :sortOrder="'desc'"
        :allowHeaderFiltering="false"
        :allowEditing="false"
        :calculate-filter-expression="calculateTransactionDateFilter"
      />

      <DxColumn
        data-field="quantity"
        :caption="$t('common.quantity')"
        data-type="number"
        :allow-sorting="false"
        :filter-operations="['contains']"
        :min-width="110"
      />

      <DxColumn
        data-field="measure_unit"
        :caption="$t('common.measure_unit')"
        data-type="string"
        :allow-sorting="false"
        :filter-operations="['contains']"
        :min-width="110"
      />

      <DxColumn
        data-field="unit_price"
        :caption="$t('common.unit_price')"
        data-type="number"
        :allow-sorting="false"
        :filter-operations="['contains']"
        :min-width="110"
      />

      <DxColumn
        data-field="total"
        :caption="$t('common.total')"
        data-type="number"
        :allow-sorting="false"
        :filter-operations="['contains']"
        :min-width="110"
      />

      <DxColumn
        data-field="source_warehouse.name"
        :caption="$t('stock_management.transactions.source_whs')"
        data-type="string"
        :allow-sorting="false"
        :filter-operations="['contains']"
        :min-width="130"
        cell-template="sourceWarehouseCell"
      />
      <template #sourceWarehouseCell="{ data }">
        <div class="warehouse-cell">
          <div class="warehouse-cell__name">{{ data.data?.source_warehouse?.name }}</div>
          <div class="warehouse-cell__unit" v-if="getWarehouseUnitName(data.data?.source_warehouse)">
            {{ getWarehouseUnitName(data.data?.source_warehouse) }}
          </div>
        </div>
      </template>

      <DxColumn
        data-field="dest_warehouse_ok.name"
        :caption="$t('stock_management.transactions.dest_whs')"
        data-type="string"
        :allow-sorting="false"
        :filter-operations="['contains']"
        :min-width="130"
        cell-template="destWarehouseCell"
      />
      <template #destWarehouseCell="{ data }">
        <div class="warehouse-cell">
          <div class="warehouse-cell__name">{{ data.data?.dest_warehouse_ok?.name }}</div>
          <div class="warehouse-cell__unit" v-if="getWarehouseUnitName(data.data?.dest_warehouse_ok)">
            {{ getWarehouseUnitName(data.data?.dest_warehouse_ok) }}
          </div>
        </div>
      </template>

      <DxColumn
        data-field="partner.name"
        :caption="$t('common.partner')"
        data-type="string"
        :allow-sorting="false"
        :filter-operations="['contains']"
        :min-width="130"
      />

      <DxColumn
        data-field="document.document_header.current_model"
        :caption="$t('stock_management.document.document_number')"
        data-type="string"
        :allow-sorting="false"
        :filter-operations="['contains']"
        :min-width="150"
      />

      <DxColumn
        data-field="work_details"
        :caption="$t('stock_management.transactions.work_details')"
        data-type="string"
        :allow-sorting="false"
        :filter-operations="['contains']"
        :min-width="190"
        cell-template="workDetailsCell"
      />
      <template #workDetailsCell="{ data }">
        <div>
          <div v-if="data.data?.work_details?.work_name">{{ data.data.work_details.work_name }}</div>
          <div v-if="data.data?.work_details?.work_machine_name">{{ data.data.work_details.work_machine_name }}</div>
          <div v-if="data.data?.work_details?.parcel_name">{{ data.data.work_details.parcel_name }}</div>
        </div>
      </template>

      <DxColumn
        data-field="shipping_note_number"
        :caption="$t('stock_management.shipping_number')"
        data-type="string"
        :allow-sorting="false"
        :filter-operations="['contains']"
        :min-width="140"
      />

      <DxColumn
        data-field="created_at"
        :caption="$t('common.created_at')"
        data-type="date"
        :allow-sorting="true"
        :filter-operations="['=']"
        :min-width="120"
        :format="createdAtDateTimeFormat"
      />

      <DxColumn
        :width="70"
        alignment="center"
        :fixed="true"
        fixed-position="right"
        :allow-sorting="false"
        :allow-filtering="false"
        :show-in-column-chooser="false"
        cell-template="actionsCell"
      />
      <template #actionsCell="{ data }">
        <DxActionMenu
          :grid-ref="$refs[gridRefKey]"
          row-key="uuid"
          :row="data.data"
          :can-edit="false"
          :can-delete="canDelete"
          :allow-deleting="allowDeletingRow"
          :delete-action="openCancelDialog"
          :merge-with-default-items="true"
          :items="getActionItems(data.data)"
          :disabled="exportPdfInProgress"
        />
      </template>

      <DxPaging :page-size="pageSize" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="pageSizes"
        display-mode="compact"
        :info-text="$t('common.page_info')"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
      />
      <DxFilterRow :visible="true" apply-filter="auto" />
      <DxGroupPanel :visible="false" :empty-panel-text="$t('common.group_panel_placeholder')" />
      <DxColumnChooser :enabled="false" mode="select" />
      <DxColumnFixing :enabled="true" />
      <DxExport :enabled="false" :allow-export-selected-data="false" />
    </DxDataGrid>

    <DeleteConfirmationDialog
      :show="showCancelConfirmation"
      @close="showCancelConfirmation = false"
      @confirm="confirmCancelTransaction"
      :title="$t('stock_management.title')"
      :message="$t('stock_management.transactions.delete_question')"
    ></DeleteConfirmationDialog>

    <EditTransactionDialog
      :visible="showEditTransactionPopup"
      :row="editTransactionRow"
      @hidden="closeEditTransactionPopup"
      @saved="onTransactionSaved"
      @save-error="handleDownloadError"
    />

    <ErpResponsePopup
      :visible="showErpResponsePopup"
      :row="erpResponseRow"
      :data="erpResponseData"
      :loading="erpResponseLoading"
      :error="erpResponseError"
      @close="closeErpResponsePopup"
    />
  </div>
  <div v-else>
    <forbidden></forbidden>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import CustomStore from "devextreme/data/custom_store";
import {
  DxColumn,
  DxDataGrid,
  DxFilterRow,
  DxPager,
  DxPaging,
  DxGroupPanel,
  DxColumnChooser,
  DxColumnFixing,
  DxExport,
  DxLookup,
} from "devextreme-vue/data-grid";
import { addMonths, format } from "date-fns";
import DxTagBox from "devextreme-vue/tag-box";
import ErpResponsePopup from "./partials/ErpResponsePopup.vue";
import { canDelete, canEdit, canView, hasRole, permissions, permissionTypes } from "@/helpers/Permissions";
import StockActions from "~/pages/stock-management/sm-stocks/partials/StockActions.vue";
import Forbidden from "~/pages/errors/Forbidden.vue";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";
import GridFilter from "@/components/DxGrid/GridFilter.vue";
import DateRangeFilter from "@/components/DxGrid/DateRangeFilter.vue";
import DxActionMenu from "@/components/DxGrid/DxActionMenu.vue";
import { dxFilterKeyExist } from "@/helpers/dxGridHelper";
import DeleteConfirmationDialog from "@/components/Modals/DeleteConfirmationDialog";
import { errorMessage, successMessage } from "@/helpers/common";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import EditTransactionDialog from "./partials/EditTransactionDialog.vue";

export default {
  name: "SMTransactions",
  middleware: ["auth", "check-company-admin"],
  data() {
    const today = new Date();
    return {
      gridAttributes: {
        class: "smagria-grid sm-transactions-grid",
      },
      gridRefKey: "smTransactionsGrid",
      filterPanelVisible: false,
      grid_page_settings: {},
      filters: {
        transaction_types: [],
        date_from: addMonths(today, -12),
        date_to: addMonths(today, 1),
      },
      transactionsDataSource: new CustomStore({
        key: "uuid",
        load: async (loadOptions) => {
          const dataSource = {
            data: [],
            totalCount: 0,
          };

          try {
            const pageLimit = loadOptions.isLoadingAll ? 99999 : loadOptions.take;
            const pageNumber = (loadOptions.skip + loadOptions.take) / pageLimit;

            const payload = {
              page: pageNumber,
              limit: pageLimit,
              filter: loadOptions.filter,
              sort: loadOptions.sort,
            };

            const transactionTypeFound = dxFilterKeyExist("transaction_type", payload.filter);

            if (this.filters.transaction_types.length > 0 && !transactionTypeFound) {
              if (!payload.filter) {
                payload.filter = [];
              }

              if (payload.filter.length > 0) {
                payload.filter.push("and");
              }

              payload.filter.push(["transaction_type", "in", this.filters.transaction_types]);
            }

            if (this.filters.date_from) {
              payload.date_from = this.filters.date_from;
            }

            if (this.filters.date_to) {
              payload.date_to = this.filters.date_to;
            }

            const response = await this.getSmTransactions(payload);
            dataSource.data = response.data ?? [];
            dataSource.totalCount = this.pagination?.total ?? 0;


            if(this.exportPdfInProgress) {
              this.exportPdfInProgress = false;
            }

            return dataSource;
          } catch (error) {
            console.error("Error fetching transactions:", error);
            throw error;
          }
        },
      }),
      gridRemoteOperations: {
        paging: true,
        filtering: true,
        sorting: true,
      },
      pageSizes: [15, 50, 100],
      pageSize: 15,
      showCancelConfirmation: false,
      cancelRow: null,
      exportPdfInProgress: false,
      showEditTransactionPopup: false,
      editTransactionRow: null,
      showErpResponsePopup: false,
      erpResponseRow: null,
      erpResponseData: null,
      erpResponseLoading: false,
      erpResponseError: null,
      visibilitySettingsLoaded: false,
      visibilitySettingsLoading: false,
      erpIntegrationEnabled: false,
      receptionIntegrationEnabled: false,
      transferIntegrationEnabled: false,
      transferSettings: {},
      yes_no_list: [
        {
          id: 1,
          label: this.$t("common.yes"),
        },
        {
          id: 0,
          label: this.$t("common.no"),
        },
      ],
    };
  },
  components: {
    Forbidden,
    StockActions,
    GridPanelHeader,
    GridFilter,
    DateRangeFilter,
    DxDataGrid,
    DxColumn,
    DxPaging,
    DxPager,
    DxFilterRow,
    DxGroupPanel,
    DxColumnChooser,
    DxColumnFixing,
    DxExport,
    DxLookup,
    DxTagBox,
    DxActionMenu,
    DeleteConfirmationDialog,
    EditTransactionDialog,
    ErpResponsePopup,
  },
  computed: {
    ...mapGetters({
      pagination: "common/pagination",
      departments: "company/departments",
      transactions: "smTransactions/smTransactions",
      smTransactionTypes: "smTransactions/smTransactionTypes",
      getDxGridDefaultDateFormat: "settings/getDxGridDefaultDateFormat",
    }),
    config() {
      return {
        permission: permissions.SM_TRANSACTIONS,
        permission_type: permissionTypes.access,
      };
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this);
    },
    canView() {
      return canView(this);
    },
    createdAtDateTimeFormat() {
      return this.getDxGridDefaultDateFormat ? `${this.getDxGridDefaultDateFormat} HH:mm` : "yyyy-MM-dd HH:mm";
    },
    filteredSmTransactionTypes() {
      const excludedUuids = new Set(["FIELD_DELIVERY_NOTE", "TRANSFER_DELIVERY_NOTE"]);
      const source = Array.isArray(this.smTransactionTypes) ? this.smTransactionTypes : [];
      return source.filter((item) => !excludedUuids.has(item?.uuid));
    },
  },
  watch: {
    grid_page_settings: {
      handler(newValue) {
        if (!newValue) {
          return;
        }

        const parsedSettings = typeof newValue === "string" ? JSON.parse(newValue) : newValue;

        if (parsedSettings && parsedSettings.filters) {
          this.filters.transaction_types = parsedSettings.filters.transaction_types || [];
          this.filters.date_from = parsedSettings.filters.date_from || addMonths(new Date(), -12);
          this.filters.date_to = parsedSettings.filters.date_to || addMonths(new Date(), 1);

          this.$nextTick(() => {
            const instance = this.$refs?.[this.gridRefKey]?.instance;
            if (instance) {
              instance.option("paging.pageIndex", 0);
              instance.refresh();
            }
          });
        }
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions({
      getSmTransactions: "smTransactions/getSmTransactions",
      getSmTransactionTypes: "smTransactions/getSmTransactionTypes",
      cancelSmTransaction: "smTransactions/cancelSmTransaction",
      getExportInfo: "smTransactions/getExportInfo",
      downloadPdf: "smTransactions/downloadPdf",
      sendReceptionTransactionToErp: "smTransactions/sendReceptionTransactionToErp",
      sendTransferTransactionToErp: "smTransactions/sendTransferTransactionToErp",
      getDxGridSettings: "dxgrid/getDxGridSettings",
      setDxGridSettings: "dxgrid/setDxGridSettings",
      getErpIntegrationEnabled: "smWarehouses/getErpIntegrationEnabled",
    }),
    ...mapActions("erpIntegration", {
      retryReceptionQueueItem: "retryReceptionQueueItem",
      fetchReceptionSettings: "fetchReceptionSettings",
      fetchReceptionQueueItem: "fetchReceptionQueueItem",
      retryTransferQueueItem: "retryTransferQueueItem",
      fetchTransferSettings: "fetchTransferSettings",
      fetchTransferQueueItem: "fetchTransferQueueItem",
      fetchConsumQueueItem: "fetchConsumQueueItem",
    }),
    hasDocumentGeneratorRole() {
      return hasRole(this, permissions.STOCK_DOCUMENT_CREATOR);
    },
    openFilterPanel() {
      this.filterPanelVisible = true;
    },
    closeFilterPanel() {
      this.filterPanelVisible = false;
    },
    applyFilters() {
      const instance = this.$refs?.[this.gridRefKey]?.instance;
      if (instance) {
        instance.refresh();
      }
      this.closeFilterPanel();
      this.updateFilterSettings();
    },
    resetFilters() {
      this.filters.transaction_types = [];
      this.filters.date_from = addMonths(new Date(), -12);
      this.filters.date_to = addMonths(new Date(), 1);
      const instance = this.$refs?.[this.gridRefKey]?.instance;
      if (instance) {
        instance.refresh();
      }
      this.closeFilterPanel();
      this.updateFilterSettings();
    },
    updateFilterSettings() {
      this.setDxGridSettings({
        grid_identifier: this.gridRefKey,
        grid_page_settings: JSON.stringify({
          filters: {
            transaction_types: this.filters.transaction_types,
            date_from: this.filters.date_from,
            date_to: this.filters.date_to,
          },
        }),
      });
    },
    allowDeletingRow(row) {
      return this.canDelete && row && !row.canceled_at;
    },
    formatDateOnly(value) {
      if (!value) return value;
      if (typeof value === "string") return value;
      return format(value, "yyyy-MM-dd");
    },
    calculateTransactionDateFilter(filterValue, selectedFilterOperation) {
      if (!filterValue) return null;
      const operation = selectedFilterOperation || "=";
      return ["transaction_date", operation, this.formatDateOnly(filterValue)];
    },
    getCanceledYesNo(rowData) {
      return rowData?.canceled_at ? 1 : 0;
    },
    getHasDocumentYesNo(rowData) {
      return rowData?.document || rowData?.cancel_document ? 1 : 0;
    },
    getWarehouseUnitName(warehouse) {
      if (!warehouse) {
        return "";
      }

      const directName = warehouse.department?.name || warehouse.department_name || warehouse.legal_entity_name;
      if (directName) {
        return directName;
      }

      const departmentUuid = warehouse.department_uuid || warehouse.department?.uuid;
      if (!departmentUuid || !Array.isArray(this.departments)) {
        return "";
      }

      return this.departments.find((department) => department?.uuid === departmentUuid)?.name || "";
    },
    getWarehouseDepartment(warehouse) {
      const departmentUuid = warehouse?.department_uuid || warehouse?.department?.uuid;

      if (!departmentUuid || !Array.isArray(this.departments)) {
        return warehouse?.department || null;
      }

      return this.departments.find((department) => department?.uuid === departmentUuid) || warehouse?.department || null;
    },
    getDepartmentErpCode(department) {
      return department?.wm_key || department?.erp_code || department?.code || department?.subunitate_code || null;
    },
    getTransferCarnetSymbol() {
      return String(this.transferSettings?.default_carnet_transfer || "").trim();
    },
    getTransferSubunitatePayload(row, transaction = null) {
      const sourceWarehouse = transaction?.source_warehouse || row?.source_warehouse || null;
      const destWarehouse = transaction?.dest_warehouse_ok || transaction?.dest_warehouse || row?.dest_warehouse_ok || row?.dest_warehouse || null;
      const sourceDepartment = this.getWarehouseDepartment(sourceWarehouse);
      const destDepartment = this.getWarehouseDepartment(destWarehouse);
      const sourceCode = this.getDepartmentErpCode(sourceDepartment);
      const destCode = this.getDepartmentErpCode(destDepartment);
      const payload = {
        source_subunit_uuid: sourceDepartment?.uuid || sourceWarehouse?.department_uuid || null,
        dest_subunit_uuid: destDepartment?.uuid || destWarehouse?.department_uuid || null,
        source_subunit_code: sourceCode,
        dest_subunit_code: destCode,
      };

      if (sourceCode && destCode && String(sourceCode) === String(destCode)) {
        payload.cod_subunitate = sourceCode;
        payload.CodSubunitate = sourceCode;
      }

      const carnetSymbol = this.getTransferCarnetSymbol();
      if (carnetSymbol) {
        payload.default_carnet_transfer = carnetSymbol;
        payload.transfer_carnet_symbol = carnetSymbol;
        payload.SimbolCarnet = carnetSymbol;
      }

      return payload;
    },
    getYesNoColumnText(value) {
      if (value === null || value === undefined) {
        return "";
      }
      return this.yes_no_list.find((item) => item.id === value)?.label;
    },
    getActionItems(row) {
      if (!row) return [];
      const items = [];

      if (this.canEdit && !row.document && row.transaction_type === "Consum") {
        items.push({
          key: "edit_consum",
          label: this.$t("common.edit"),
          text: this.$t("common.edit"),
          icon: require("@/assets/images/icons/edit.svg"),
          class: "",
          action: () => this.openEditTransactionPopup(row),
        });
      }

      if (this.hasDocumentGeneratorRole()) {
        if (row.canceled_at && row.cancel_export_url && !row.cancel_document) {
          items.push({
            key: "generate_cancel_pdf",
            label: this.$t("stock_management.transactions.generate_cancel_pdf", { type: row.type_name }),
            text: this.$t("stock_management.transactions.generate_cancel_pdf", { type: row.type_name }),
            icon: require("@/assets/images/icons/transfer.svg"),
            class: "",
            action: () => this.handleExportPdf(row, true),
          });
        }

        if (!row.canceled_at && row.export_url && !row.document) {
          items.push({
            key: "generate_pdf",
            label: this.$t("stock_management.transactions.generate_pdf", { type: row.type_name }),
            text: this.$t("stock_management.transactions.generate_pdf", { type: row.type_name }),
            icon: require("@/assets/images/icons/transfer.svg"),
            class: "",
            action: () => this.handleExportPdf(row, false),
          });
        }
      }

      if (row.document && this.isErpResendAvailable(row)) {
        const hasQueueItem = !!row?.erp_export?.uuid;
        const erpLabel = hasQueueItem
          ? this.$t("stock_management.transactions.erp_resend")
          : this.$t("stock_management.transactions.erp_send");

        items.push({
          key: "erp_resend",
          label: erpLabel,
          text: erpLabel,
          icon: require("@/assets/images/icons/transfer.svg"),
          class: "",
          action: () => this.onResendToErp(row),
        });
      }

      if (this.isReceptionTransaction(row) && this.isErpConfirmed(row) && row?.erp_export?.uuid) {
        items.push({
          key: "view_erp_response",
          label: this.$t("stock_management.transactions.view_erp_response"),
          text: this.$t("stock_management.transactions.view_erp_response"),
          icon: require("@/assets/images/icons/details_file.svg"),
          class: "",
          action: () => this.openErpResponsePopup(row),
        });
      }

      if (this.isTransferTransaction(row) && this.isErpConfirmed(row) && row?.erp_export?.uuid) {
        items.push({
          key: "view_erp_response",
          label: this.$t("stock_management.transactions.view_erp_response"),
          text: this.$t("stock_management.transactions.view_erp_response"),
          icon: require("@/assets/images/icons/details_file.svg"),
          class: "",
          action: () => this.openErpResponsePopup(row),
        });
      }

      return items;
    },
    openEditTransactionPopup(row) {
      this.editTransactionRow = row;
      this.showEditTransactionPopup = true;
    },
    closeEditTransactionPopup() {
      this.showEditTransactionPopup = false;
      this.editTransactionRow = null;
    },
    async openErpResponsePopup(row) {
      this.erpResponseRow = row;
      this.erpResponseLoading = true;
      this.erpResponseError = null;
      this.erpResponseData = null;
      this.showErpResponsePopup = true;
      try {
        const item = this.isTransferTransaction(row)
          ? await this.fetchTransferQueueItem(row.erp_export.uuid)
          : (this.isConsumTransaction(row)
            ? await this.fetchConsumQueueItem(row.erp_export.uuid)
            : await this.fetchReceptionQueueItem(row.erp_export.uuid));
        this.erpResponseData = item;
      } catch (e) {
        this.erpResponseError = e;
      } finally {
        this.erpResponseLoading = false;
      }
    },
    closeErpResponsePopup() {
      this.showErpResponsePopup = false;
      this.erpResponseRow = null;
      this.erpResponseData = null;
      this.erpResponseLoading = false;
      this.erpResponseError = null;
    },
    onTransactionSaved() {
      this.closeEditTransactionPopup();
      this.reloadGrid();
    },
    onRowPrepared(e) {
      if (e.rowType === "data" && e.data && e.data.canceled_at) {
        e.rowElement.classList.add("smagria-deleted-row");
      }
    },
    getCanceledBool(rowData) {
      return !!rowData?.canceled_at;
    },
    getTransactionTypeName(rowData) {
      const typeUuid = rowData?.transaction_type_uuid;
      const typeObj = this.smTransactionTypes.find((type) => type.uuid === typeUuid);
      return typeObj ? typeObj.name : "";
    },
    openCancelDialog(row) {
      if (!row || !this.allowDeletingRow(row)) return;
      this.cancelRow = row;
      this.showCancelConfirmation = true;
    },
    async confirmCancelTransaction() {
      if (!this.cancelRow) return;
      try {
        await this.cancelSmTransaction({ uuid: this.cancelRow.uuid });
        successMessage(this, this.$t("delete_success"));
        this.showCancelConfirmation = false;
        this.cancelRow = null;
        this.reloadGrid();
      } catch (error) {
        console.error("Error canceling transaction:", error);
        this.showCancelConfirmation = false;
        if (!error?.response) {
          errorMessage(this, this.$t("error_alert_text"), error);
        }
      }
    },
    reloadGrid() {
      const instance = this.$refs?.[this.gridRefKey]?.instance;
      if (instance) {
        instance.getDataSource()?.reload?.();
      }
    },
    async handleDownloadPdf(row, isCancel) {
      try {
        //await this.triggerDownload(row, isCancel, "application/json");
        await this.triggerDownload(row, isCancel, "blob");
      } catch (e) {
        this.handleDownloadError(e);
      }
    },
    async handleDownloadDeliveryNote(row) {
      try {
        await this.downloadPdf({
          type: row.delivery_note_document_uuid,
          url: row.delivery_note_download_url,
          filename: row.delivery_note_download_filename,
          response_type: "blob",
        });
      } catch (e) {
        this.handleDownloadError(e);
      }
    },
    async triggerDownload(row, isCancel, responseType) {
      return this.downloadPdf({
        type: isCancel ? row.cancel_document_uuid : row.document_uuid,
        url: isCancel ? row.cancel_download_url : row.download_url,
        filename: isCancel ? row.cancel_download_filename : row.download_filename,
        response_type: responseType,
      });
    },
    handleDownloadError(e) {
      if (e?.response && (e.response.status === 400 || e.response.status === 500)) {
        const error = e.response.data?.errors?.error ? e.response.data.errors.error : this.$t("error_alert_text");
        errorMessage(this, error);
      }
    },
    normalizeTransactionType(type) {
      if (!type || typeof type !== "string") {
        return "";
      }

      return type
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    },
    isReceptionExport(row, exportResponse, transaction, isCancel) {
      if (isCancel) {
        return false;
      }

      const typeCandidates = [
        exportResponse?.data?.transaction_type,
        transaction?.transaction_type,
        row?.transaction_type,
        row?.type_name,
      ]
        .map((type) => this.normalizeTransactionType(type))
        .filter(Boolean);

      return typeCandidates.some((type) => type.includes("reception") || type.includes("receptie") || type.includes("recepcio"));
    },
    hasErpErrorField(value) {
      return value !== null && value !== undefined && value !== "";
    },
    extractBackendErrorMessage(error) {
      // errors.error holds the real underlying reason (e.g. per-entity nomenclator mapping detail);
      // the top-level `message` is often a generic wrapper string, so only fall back to it when no detail exists
      const queueError = error?.response?.data?.data?.last_error_message;
      const errorBag = error?.response?.data?.errors?.error;
      const entries = Array.isArray(errorBag) ? errorBag : errorBag ? [errorBag] : [];
      const detail = entries.flat().filter(Boolean).join(" | ");

      return (
        queueError ||
        detail ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        ""
      );
    },
    getErpExportFailureDialogMessage(erpExportResponse, fallbackSourceUuid = null) {
      const failedStatuses = ["ERROR", "DEAD", "CANCELED"];
      const queueItemsCandidates = [
        erpExportResponse?.data?.queue_item,
        erpExportResponse?.data?.queue_items,
        erpExportResponse?.data?.items,
        erpExportResponse?.data?.data,
        erpExportResponse?.data,
        erpExportResponse?.queue_item,
        erpExportResponse?.queue_items,
        erpExportResponse?.items,
        erpExportResponse,
      ];

      const queueItems = queueItemsCandidates.reduce((list, candidate) => {
        if (Array.isArray(candidate)) {
          return list.concat(candidate);
        }

        if (
          candidate &&
          typeof candidate === "object" &&
          (candidate.status || this.hasErpErrorField(candidate.last_error_code) || this.hasErpErrorField(candidate.last_error_message))
        ) {
          list.push(candidate);
        }

        return list;
      }, []);

      const failedItem = queueItems.find((item) => {
        const status = (item?.status || "").toUpperCase();
        return (
          failedStatuses.includes(status) ||
          this.hasErpErrorField(item?.last_error_code) ||
          this.hasErpErrorField(item?.last_error_message)
        );
      });

      if (!failedItem) {
        // Check for ERP validation ErrorList directly in response body (HTTP 200 with result: "error")
        if (erpExportResponse?.data?.result === "error" && Array.isArray(erpExportResponse?.data?.ErrorList)) {
          const errorMessages = erpExportResponse.data.ErrorList.join("\n");
          return errorMessages;
        }
        return null;
      }

      // last_error_message is already a clean, itemized, user-facing text - show it as-is instead of wrapping it in a technical sentence
      if (this.hasErpErrorField(failedItem?.last_error_message)) {
        return failedItem.last_error_message;
      }

      const errorDetails = failedItem?.last_error_code || this.$t("stock_management.reception.erp_send_failure_unknown_reason");

      return this.$t("stock_management.reception.erp_send_failure_dialog_message", {
        source_uuid: failedItem?.source_uuid || fallbackSourceUuid || this.$t("unknown"),
        status: failedItem?.status || this.$t("unknown"),
        error: errorDetails,
      });
    },
    async showErpSendErrorDialog(message) {
      // the dialog plugin renders html, so turn plain-text line breaks into <br> for the itemized message
      const htmlMessage = String(message || "").replace(/\n/g, "<br>");
      await this.$dialog.alert(htmlMessage).then((dialog) => {
        dialog.close();
      });
    },
    async trySendReceptionToErpNow(row, transaction, exportResponse, isCancel) {
      if (!this.isReceptionExport(row, exportResponse, transaction, isCancel)) {
        return;
      }

      if (!this.erpIntegrationEnabled || !this.receptionIntegrationEnabled) {
        return;
      }

      const sourceUuid = transaction?.uuid || row?.uuid;
      if (!sourceUuid) {
        return;
      }

      try {
        const erpExportResponse = await this.sendReceptionTransactionToErp({
          source_type: "SM_RECEPTION",
          source_uuid: sourceUuid,
          source_document_number: transaction?.shipping_note_number || row?.shipping_note_number || null,
          source_event_at: transaction?.transaction_date || row?.transaction_date || null,
          send_mode: "TRY_NOW",
        });

        const failureDialogMessage = this.getErpExportFailureDialogMessage(erpExportResponse, sourceUuid);
        if (failureDialogMessage) {
          errorMessage(this, this.$t("stock_management.reception.erp_send_error"));
          await this.showErpSendErrorDialog(failureDialogMessage);
        }
      } catch (e) {
        errorMessage(this, this.$t("stock_management.reception.erp_send_error"));
        const backendMessage = this.extractBackendErrorMessage(e);
        await this.showErpSendErrorDialog(
          backendMessage
            ? this.$t("stock_management.reception.erp_send_exception_dialog_message", { error: backendMessage })
            : this.$t("stock_management.reception.erp_send_error")
        );
      }
    },
    async trySendTransferToErpNow(row, transaction, exportResponse, isCancel) {
      if (!this.isTransferExport(row, exportResponse, transaction, isCancel)) {
        return;
      }

      if (!this.erpIntegrationEnabled || !this.transferIntegrationEnabled) {
        return;
      }

      const sourceUuid = transaction?.uuid || row?.uuid;
      if (!sourceUuid) {
        return;
      }

      try {
        const erpExportResponse = await this.sendTransferTransactionToErp({
          source_type: "SM_TRANSFER",
          source_uuid: sourceUuid,
          source_document_number: transaction?.document?.header?.document_number || row?.document?.document_number || null,
          source_event_at: transaction?.transaction_date || row?.transaction_date || null,
          send_mode: "TRY_NOW",
          ...this.getTransferSubunitatePayload(row, transaction),
        });

        const failureDialogMessage = this.getErpExportFailureDialogMessage(erpExportResponse, sourceUuid);
        if (failureDialogMessage) {
          errorMessage(this, this.$t("stock_management.transfer.erp_send_error"));
          await this.showErpSendErrorDialog(failureDialogMessage);
        }
      } catch (e) {
        errorMessage(this, this.$t("stock_management.transfer.erp_send_error"));
        const backendMessage = this.extractBackendErrorMessage(e);
        await this.showErpSendErrorDialog(
          backendMessage
            ? this.$t("stock_management.transfer.erp_send_exception_dialog_message", { error: backendMessage })
            : this.$t("stock_management.transfer.erp_send_error")
        );
      }
    },
    async loadErpIntegrationStatus() {
      try {
        const erpResp = await this.getErpIntegrationEnabled();
        const val = erpResp?.data?.value ?? erpResp?.value ?? erpResp?.data;
        this.erpIntegrationEnabled = val === "1" || val === 1 || val === true;

        if (!this.erpIntegrationEnabled) {
          return;
        }

        const [receptionSettings, transferSettings] = await Promise.allSettled([
          this.fetchReceptionSettings(),
          this.fetchTransferSettings(),
        ]);

        if (receptionSettings.status === "fulfilled") {
          const enabled = receptionSettings.value?.reception_integration_enabled;
          this.receptionIntegrationEnabled = enabled === true || enabled === "1" || enabled === 1;
        }

        if (transferSettings.status === "fulfilled") {
          const enabled = transferSettings.value?.transfer_integration_enabled;
          this.transferIntegrationEnabled = enabled === true || enabled === "1" || enabled === 1;
          this.transferSettings = transferSettings.value || {};
        }
      } catch (e) {
        console.error("Error loading ERP integration status:", e);
      }
    },
    isReceptionTransaction(row) {
      if (!row) return false;
      const typeCandidates = [
        row?.type_name,
        row?.transaction_type,
        row?.type,
      ]
        .map((type) => this.normalizeTransactionType(type))
        .filter(Boolean);

      return typeCandidates.some(
        (type) =>
          type.includes("reception") ||
          type.includes("receptie") ||
          type.includes("recepcio") ||
          type.includes("nota de receptie")
      );
    },
    isTransferTransaction(row) {
      if (!row) return false;
      const typeCandidates = [
        row?.type_name,
        row?.transaction_type,
        row?.type,
        row?.transaction_type_uuid,
      ]
        .map((type) => this.normalizeTransactionType(type))
        .filter(Boolean);

      return typeCandidates.some(
        (type) =>
          type.includes("transfer") ||
          type.includes("bon transfer") ||
          type.includes("atestat")
      );
    },
    isConsumTransaction(row) {
      if (!row) return false;
      const typeCandidates = [
        row?.type_name,
        row?.transaction_type,
        row?.type,
        row?.transaction_type_uuid,
      ]
        .map((type) => this.normalizeTransactionType(type))
        .filter(Boolean);

      return typeCandidates.some(
        (type) =>
          type === "consum" ||
          type === "consumption" ||
          type === "fogyasztas" ||
          type.includes("consum") ||
          type.includes("fogyaszt")
      );
    },
    isTransferExport(row, exportResponse, transaction, isCancel) {
      if (isCancel) {
        return false;
      }

      return (
        this.isTransferTransaction(row) ||
        this.isTransferTransaction(transaction) ||
        this.isTransferTransaction(exportResponse?.data?.transaction) ||
        this.isTransferTransaction(exportResponse?.data)
      );
    },
    shouldShowErpStatus(row) {
      if (!this.erpIntegrationEnabled || !row?.document) {
        return false;
      }
      if (this.receptionIntegrationEnabled && this.isReceptionTransaction(row)) {
        return true;
      }
      if (this.transferIntegrationEnabled && this.isTransferTransaction(row)) {
        return true;
      }
      return false;
    },
    isErpSuccess(row) {
      const status = (row?.erp_export?.status || "").toUpperCase();
      return ["SENT", "CONFIRMED"].includes(status);
    },
    isErpError(row) {
      const status = (row?.erp_export?.status || "").toUpperCase();
      return ["ERROR", "DEAD"].includes(status);
    },
    isErpConfirmed(row) {
      return (row?.erp_export?.status || "").toUpperCase() === "CONFIRMED";
    },
    getErpStatusBadgeClass(row) {
      if (this.isErpSuccess(row)) {
        return "erp-status-badge--success";
      }
      if (this.isErpError(row)) {
        return "erp-status-badge--error";
      }
      return "erp-status-badge--pending";
    },
    getErpStatusTitle(row) {
      const status = (row?.erp_export?.status || "").toUpperCase();
      if (["SENT", "CONFIRMED"].includes(status)) {
        return this.$t("stock_management.transactions.erp_status_sent");
      }
      if (["ERROR", "DEAD"].includes(status)) {
        const errorDetail = row?.erp_export?.last_error_message || row?.erp_export?.last_error_code;
        return errorDetail
          ? `${this.$t("stock_management.transactions.erp_status_error")}:\n${errorDetail}`
          : this.$t("stock_management.transactions.erp_status_error");
      }
      if (status === "RETRY_WAITING") {
        return this.$t("stock_management.transactions.erp_status_retry_waiting");
      }
      return this.$t("stock_management.transactions.erp_status_pending");
    },
    getPdfLinkTitle(row, isCancel) {
      const base = isCancel
        ? (row.type_name + "\n" + this.$t("common.download_document_pdf", { document: row.cancel_document?.document_header?.current_model }))
        : (row.type_name + "\n" + this.$t("common.download_document_pdf", { document: row.document?.document_header?.current_model }));

      if (!isCancel && this.shouldShowErpStatus(row)) {
        return `${base}\n\n${this.getErpStatusTitle(row)}`;
      }

      return base;
    },
    isErpResendAvailable(row) {
      if (!this.shouldShowErpStatus(row)) {
        return false;
      }
      const status = (row?.erp_export?.status || "").toUpperCase();
      return !["SENT", "CONFIRMED"].includes(status);
    },
    async onResendToErp(row) {
      const isTransfer = this.isTransferTransaction(row);
      const isReception = this.isReceptionTransaction(row);

      try {
        let erpExportResponse = null;

        if (isTransfer) {
          if (row?.erp_export?.uuid) {
            erpExportResponse = await this.retryTransferQueueItem({
              uuid: row.erp_export.uuid,
              mode: "IMMEDIATE",
              ...this.getTransferSubunitatePayload(row),
            });
          } else {
            erpExportResponse = await this.sendTransferTransactionToErp({
              source_type: "SM_TRANSFER",
              source_uuid: row.uuid,
              source_document_number: row.document?.document_number || null,
              source_event_at: row.transaction_date || null,
              send_mode: "TRY_NOW",
              ...this.getTransferSubunitatePayload(row),
            });
          }

          const failureDialogMessage = this.getErpExportFailureDialogMessage(erpExportResponse, row.uuid);

          if (failureDialogMessage) {
            errorMessage(this, this.$t("stock_management.transfer.erp_send_error"));
            await this.showErpSendErrorDialog(failureDialogMessage);
            return;
          }

          successMessage(this, this.$t("stock_management.transfer.erp_integration_action_done"));
        } else if (isReception) {
          if (row?.erp_export?.uuid) {
            erpExportResponse = await this.retryReceptionQueueItem({
              uuid: row.erp_export.uuid,
              mode: "IMMEDIATE",
            });
          } else {
            erpExportResponse = await this.sendReceptionTransactionToErp({
              source_type: "SM_RECEPTION",
              source_uuid: row.uuid,
              source_document_number: row.shipping_note_number || null,
              source_event_at: row.transaction_date || null,
              send_mode: "TRY_NOW",
            });
          }

          const failureDialogMessage = this.getErpExportFailureDialogMessage(erpExportResponse, row.uuid);

          if (failureDialogMessage) {
            errorMessage(this, this.$t("stock_management.reception.erp_send_error"));
            await this.showErpSendErrorDialog(failureDialogMessage);
            return;
          }

          successMessage(this, this.$t("stock_management.reception.erp_integration_action_done"));
        }
      } catch (e) {
        const errorText = isTransfer
          ? this.$t("stock_management.transfer.erp_send_error")
          : this.$t("stock_management.reception.erp_send_error");
        errorMessage(this, errorText);
        const backendMessage = this.extractBackendErrorMessage(e);
        await this.showErpSendErrorDialog(
          backendMessage
            ? backendMessage
            : errorText
        );
      } finally {
        this.reloadGrid();
      }
    },
    handleExportPdf(row, isCancel) {
      if (this.exportPdfInProgress) {
        return;
      }
      this.exportPdfInProgress = true;
      this.getExportInfo({
        type: row.transaction_type,
        url: isCancel ? row.cancel_export_url : row.export_url,
        response_type: "application/json",
      })
        .then(async (resp) => {
          const transaction = resp.data?.transaction;

          await this.trySendReceptionToErpNow(row, transaction, resp, isCancel);
          await this.trySendTransferToErpNow(row, transaction, resp, isCancel);

          return this.downloadPdf({
            type: resp.data?.transaction_type,
            url: isCancel ? transaction?.cancel_download_url : transaction?.download_url,
            filename: isCancel ? transaction?.cancel_download_filename : transaction?.download_filename,
            response_type: "blob",
          });
        })
        .then(() => {
          this.reloadGrid();
        })
        .catch((e) => {
          this.exportPdfInProgress = false;

          if (e?.response?.status === 400 || e?.response?.status === 500) {
            if (e.response.data?.errors?.error) {
              errorMessage(this, e.response.data.errors.error);
            } else {
              errorMessage(this, this.$t("error_alert_text"));
            }
          }
        })
        .finally(() => {
          //this.exportPdfInProgress = false;
        });
    },
    onOptionChanged(e) {
      if (e && e.fullName === "paging.pageSize" && e.value !== e.previousValue) {
        this.pageSize = e.value;
        const instance = this.$refs?.[this.gridRefKey]?.instance;
        if (instance) {
          instance.option("paging.pageIndex", 0);
          instance.getDataSource()?.reload?.();
        }
      }
    },
    onExporting(e) {
      e.fileName = "SmTransactions_" + format(new Date(), "yyyy_MM_dd_hh_mm_ss");

      if (e.format === "xlsx") {
        const fields = ["canceled_at", "has_document"];
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("SmTransactions");
        exportDataGrid({
          component: e.component,
          worksheet,
          autoFilterEnabled: true,
          customizeCell: ({ gridCell, excelCell }) => {
            // Skip processing for non-data rows (headers, summaries, etc.)
            if (!gridCell?.data) {
              return;
            }

            if (gridCell?.column?.dataField === "transaction_type") {
              excelCell.value = this.getTransactionTypeName(gridCell?.data);
            } else if (gridCell?.column?.dataField == "work_details") {
              excelCell.value = [
                gridCell?.data?.work_details?.work_name,
                gridCell?.data?.work_details?.work_machine_name,
                gridCell?.data?.work_details?.parcel_name,
              ]
                .filter((part) => !!part)
                .join("\n");
            } else if (gridCell?.column?.dataField === "source_warehouse.name") {
              excelCell.value = [gridCell?.data?.source_warehouse?.name, this.getWarehouseUnitName(gridCell?.data?.source_warehouse)]
                .filter((part) => !!part)
                .join("\n");
            } else if (gridCell?.column?.dataField === "dest_warehouse_ok.name") {
              excelCell.value = [gridCell?.data?.dest_warehouse_ok?.name, this.getWarehouseUnitName(gridCell?.data?.dest_warehouse_ok)]
                .filter((part) => !!part)
                .join("\n");
            } else if (gridCell?.column?.dataField == "has_document") {
              excelCell.value = this.getYesNoColumnText(this.getHasDocumentYesNo(gridCell?.data));
            } else if (fields.includes(gridCell?.column?.dataField)) {
              excelCell.value = this.getYesNoColumnText(!!gridCell?.data?.[gridCell?.column?.dataField]);
            }
          },
        }).then(() => {
          workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(new Blob([buffer], { type: "application/octet-stream" }), e.fileName + ".xlsx");
          });
        });
      }
    },
  },
  mounted() {
    this.getSmTransactionTypes({
      pagination: 0,
    });

    this.loadErpIntegrationStatus();

    this.getDxGridSettings({
      grid_identifier: this.gridRefKey,
    }).then((result) => {
      if (result && result[0] && result[0].grid_page_settings) {
        this.grid_page_settings = result[0].grid_page_settings;
      }
    });
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/components/dxSmagriaGrid.scss";
</style>

<style scoped lang="scss">
.warehouse-cell {
  display: flex;
  flex-direction: column;
  line-height: 1.25;

  &__unit {
    font-size: 0.85em;
    opacity: 0.7;
  }
}

.transaction-pdf-link {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  .erp-status-badge {
    position: absolute;
    bottom: -4px;
    right: -6px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1.5px solid #ffffff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
    z-index: 2;

    svg {
      display: block;
    }

    &--success {
      background-color: #2e7d32;
      color: #ffffff;
    }

    &--error {
      background-color: #d32f2f;
      color: #ffffff;
    }

    &--pending {
      background-color: #f57c00;
      color: #ffffff;
    }
  }
}
</style>
